/**
 * Terrain Serialization — scorch marks, base64 grid export/import.
 */

import { TERRAIN, CRATER, CELL } from '../constants';
import {
  getGrid, getScorchMap, getScorchDecayTimer, setScorchDecayTimer,
  createGrid, rebuildColumnTopCache,
} from './grid-core';
import { packR2, unpackR2, unpackR1, unpackLegacy } from './grid-codec';

const { CELL_SIZE } = TERRAIN;

// ─── Scorch marks — terrain damage memory ───────────────────────────────────────

export function applyScorch(wcx: number, wcy: number, wRadius: number): void {
  const scorchMap = getScorchMap();
  const grid = getGrid();
  if (!scorchMap || !grid) return;

  const scorchRadius = wRadius * CRATER.SCORCH_RADIUS_MULT;
  const gcx = wcx / CELL_SIZE;
  const gcy = wcy / CELL_SIZE;
  const gScorchRadius = scorchRadius / CELL_SIZE;

  const minGx = Math.max(0, Math.floor(gcx - gScorchRadius));
  const maxGx = Math.min(TERRAIN.GRID_WIDTH - 1, Math.ceil(gcx + gScorchRadius));
  const minGy = Math.max(0, Math.floor(gcy - gScorchRadius));
  const maxGy = Math.min(TERRAIN.GRID_HEIGHT - 1, Math.ceil(gcy + gScorchRadius));

  for (let gy = minGy; gy <= maxGy; gy++) {
    const dy = gy - gcy;
    for (let gx = minGx; gx <= maxGx; gx++) {
      const dx = gx - gcx;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > gScorchRadius) continue;
      const idx = gy * TERRAIN.GRID_WIDTH + gx;
      if (grid[idx] === 0) continue; // only scorch solid cells

      const intensity = Math.round(255 * (1 - dist / gScorchRadius));
      scorchMap[idx] = Math.max(scorchMap[idx], intensity);
    }
  }
}

export function getScorchAt(gx: number, gy: number): number {
  const scorchMap = getScorchMap();
  if (!scorchMap) return 0;
  return scorchMap[gy * TERRAIN.GRID_WIDTH + gx];
}

export function updateScorchDecay(dt: number): boolean {
  const scorchMap = getScorchMap();
  if (!scorchMap) return false;

  const timer = getScorchDecayTimer() + dt;
  if (timer < CRATER.SCORCH_DECAY_INTERVAL) {
    setScorchDecayTimer(timer);
    return false;
  }
  setScorchDecayTimer(0);

  let anyDirty = false;
  for (let i = 0; i < scorchMap.length; i++) {
    if (scorchMap[i] > 0) {
      scorchMap[i] = Math.max(0, scorchMap[i] - CRATER.SCORCH_DECAY_AMOUNT);
      anyDirty = true;
    }
  }
  return anyDirty;
}

export function resetScorchMap(): void {
  const scorchMap = getScorchMap();
  if (scorchMap) scorchMap.fill(0);
  setScorchDecayTimer(0);
}

// ─── Base64 Serialization (Multiplayer Sync) ────────────────────────────────────

/**
 * Export grid as base64 string for network sync.
 * R2 format: 2-bit packing (4 cells/byte) → RLE → base64.
 * Supports cell values 0-3 (EMPTY=0, SAND=1, WIREFRAME=2).
 */
export function exportGridBase64(): string {
  const grid = getGrid();
  if (!grid) return '';
  return packR2(grid);
}

/**
 * Import grid from base64 string (counterpart to exportGridBase64).
 * Supports R2 (2-bit+RLE), R1 (1-bit+RLE), and legacy (1-bit only) formats.
 */
export function importGridBase64(base64: string): void {
  let grid = getGrid();
  if (!grid) {
    createGrid();
    grid = getGrid();
  }

  if (base64.startsWith('R2')) {
    unpackR2(base64, grid!);
  } else if (base64.startsWith('R1')) {
    unpackR1(base64, grid!);
  } else {
    unpackLegacy(base64, grid!);
  }

  rebuildColumnTopCache();
}
